import React, { useMemo, useState, Fragment } from 'react';
import { useData } from '../../store/DataContext';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Select } from '../../components/ui/Select';
import { ChevronDownIcon, ChevronUpIcon, PlusIcon, SendIcon, BanIcon } from 'lucide-react';
import { formatCurrency } from './pharmacyUtils';
type DraftLine = {
  medicineId: string;
  quantity: string;
  unitCost: string;
};
const newLine = (): DraftLine => ({
  medicineId: '',
  quantity: '',
  unitCost: ''
});
export function PurchaseOrders() {
  const {
    suppliers,
    medicines,
    purchaseOrders,
    createPurchaseOrder,
    updatePurchaseOrder,
    cancelPurchaseOrder,
    currentUser
  } = useData();
  const [showForm, setShowForm] = useState(false);
  const [supplierId, setSupplierId] = useState('');
  const [expectedDate, setExpectedDate] = useState('');
  const [notes, setNotes] = useState('');
  const [lines, setLines] = useState<DraftLine[]>([newLine()]);
  const [error, setError] = useState('');
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [cancellingId, setCancellingId] = useState<string | null>(null);
  const manager = currentUser?.role === 'Pharmacy Manager';
  const orders = useMemo(() => [...purchaseOrders].sort((a, b) => new Date(b.dateReceived || b.createdAt).getTime() - new Date(a.dateReceived || a.createdAt).getTime()), [purchaseOrders]);
  const updateLine = (index: number, key: keyof DraftLine, value: string) => setLines(current => current.map((line, itemIndex) => itemIndex === index ? {
    ...line,
    [key]: value
  } : line));
  const cancelOrder = async (id: string) => {
    const reason = window.prompt('Enter the mandatory cancellation reason. The purchase order will remain in the audit register.');
    if (!reason) return;
    setCancellingId(id);
    const result = await cancelPurchaseOrder(id, reason);
    setCancellingId(null);
    if (!result.ok) setError(result.error || 'Unable to cancel purchase order.');
  };
  const save = (status: 'Draft' | 'Pending') => {
    const supplier = suppliers.find(item => item.id === supplierId);
    const parsedLines = lines.filter(line => line.medicineId && Number(line.quantity) > 0 && Number(line.unitCost) >= 0).map(line => {
      const medicine = medicines.find(item => item.id === line.medicineId)!;
      return {
        medicineId: medicine.id,
        medicineName: medicine.name,
        quantityOrdered: Number(line.quantity),
        quantityReceived: 0,
        unitCost: Number(line.unitCost)
      };
    });
    if (!supplier || !parsedLines.length) return setError('Select a supplier and add at least one valid medicine line.');
    createPurchaseOrder({
      supplierId: supplier.id,
      supplierName: supplier.name,
      status,
      orderedAt: new Date().toISOString(),
      expectedDate: expectedDate || undefined,
      notes: notes || undefined,
      lines: parsedLines,
      createdBy: currentUser?.id || '',
      kind: 'Planning'
    });
    setShowForm(false);
    setSupplierId('');
    setExpectedDate('');
    setNotes('');
    setLines([newLine()]);
    setError('');
  };
  return <div className="space-y-5" data-id="element-5114">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between" data-id="element-5115">
        <div data-id="element-5116">
          <h2 className="text-xl font-bold text-slate-950" data-id="element-5117">Purchase Orders</h2>
          <p className="mt-1 text-sm text-slate-500" data-id="element-5118">
            Planning orders and completed delivery receipts share one live
            procurement register.
          </p>
        </div>
        <Button onClick={() => setShowForm(true)} className="bg-teal-600 hover:bg-teal-700" data-id="element-5119">
          <PlusIcon className="mr-2 h-4 w-4" data-id="element-5120" /> New purchase order
        </Button>
      </div>
      {showForm && <section className="rounded-xl border border-teal-200 bg-teal-50/50 p-5" data-id="element-5121">
          <div className="mb-4 flex justify-between" data-id="element-5122">
            <div data-id="element-5123">
              <h3 className="font-bold text-slate-900" data-id="element-5124">New purchase order</h3>
              <p className="mt-1 text-xs text-slate-500" data-id="element-5125">
                Use Receive New Stock to turn delivered items into an audited
                receipt.
              </p>
            </div>
            <button type="button" onClick={() => setShowForm(false)} className="text-sm font-semibold text-slate-500 hover:text-slate-900" data-id="element-5126">
              Cancel
            </button>
          </div>
          {error && <p role="alert" className="mb-3 text-sm text-red-600" data-id="element-5127">
              {error}
            </p>}
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2" data-id="element-5128">
            <Select label="Supplier" value={supplierId} onChange={event => setSupplierId(event.target.value)} options={[{
          value: '',
          label: 'Select supplier'
        }, ...suppliers.filter(supplier => supplier.status === 'Active').map(supplier => ({
          value: supplier.id,
          label: supplier.name
        }))]} data-id="element-5129" />
            <Input label="Expected date" type="date" value={expectedDate} onChange={event => setExpectedDate(event.target.value)} data-id="element-5130" />
          </div>
          <div className="mt-4 overflow-x-auto" data-id="element-5131">
            <table className="min-w-[680px] w-full" data-id="element-5132">
              <thead data-id="element-5133">
                <tr className="text-left text-xs font-semibold uppercase tracking-wide text-slate-500" data-id="element-5134">
                  <th className="pb-2" data-id="element-5135">Medicine</th>
                  <th className="pb-2" data-id="element-5136">Quantity</th>
                  <th className="pb-2" data-id="element-5137">Buying price</th>
                </tr>
              </thead>
              <tbody data-id="element-5138">
                {lines.map((line, index) => <tr key={index} data-id="element-5139">
                    <td className="min-w-64 pb-2 pr-3" data-id="element-5140">
                      <select aria-label="Medicine" value={line.medicineId} onChange={event => {
                  const medicine = medicines.find(item => item.id === event.target.value);
                  updateLine(index, 'medicineId', event.target.value);
                  if (!manager && medicine) updateLine(index, 'unitCost', String(medicine.buyingPrice));
                }} className="w-full rounded-md border border-slate-300 bg-white p-2 text-sm" data-id="element-5141">
                        <option value="" data-id="element-5142">Select medicine</option>
                        {medicines.map(medicine => <option key={medicine.id} value={medicine.id} data-id="element-5143">
                            {medicine.name}
                          </option>)}
                      </select>
                    </td>
                    <td className="pb-2 pr-3" data-id="element-5144">
                      <input aria-label="Quantity" type="number" min="1" value={line.quantity} onChange={event => updateLine(index, 'quantity', event.target.value)} className="w-28 rounded-md border border-slate-300 p-2 text-sm" data-id="element-5145" />
                    </td>
                    <td className="pb-2" data-id="element-5146">
                      <input aria-label="Buying price" type="number" min="0" step="0.01" value={line.unitCost} disabled={!manager} onChange={event => updateLine(index, 'unitCost', event.target.value)} className="w-32 rounded-md border border-slate-300 p-2 text-sm" data-id="element-5147" />
                    </td>
                  </tr>)}
              </tbody>
            </table>
          </div>
          <button type="button" onClick={() => setLines(current => [...current, newLine()])} className="mt-2 text-sm font-semibold text-teal-700 hover:text-teal-900" data-id="element-5148">
            + Add medicine line
          </button>
          <label className="mt-4 block text-sm font-medium text-slate-700" data-id="element-5149">
            Notes
            <textarea value={notes} onChange={event => setNotes(event.target.value)} rows={2} className="mt-1 block w-full rounded-md border border-slate-300 p-2 text-sm" data-id="element-5150" />
          </label>
          <div className="mt-4 flex justify-end gap-2" data-id="element-5151">
            <Button variant="outline" onClick={() => save('Draft')} data-id="element-5152">
              Save draft
            </Button>
            <Button onClick={() => save('Pending')} className="bg-teal-600 hover:bg-teal-700" data-id="element-5153">
              <SendIcon className="mr-2 h-4 w-4" data-id="element-5154" /> Submit order
            </Button>
          </div>
        </section>}
      <section className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm" data-id="element-5155">
        <div className="overflow-x-auto" data-id="element-5156">
          <table className="min-w-[1050px] w-full divide-y divide-slate-100" data-id="element-5157">
            <thead className="bg-slate-50" data-id="element-5158">
              <tr data-id="element-5159">
                {['Order / receipt', 'Supplier & invoice', 'Received', 'Total cost', 'Status', ''].map(heading => <th key={heading} className="px-5 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-500" data-id="element-5160">
                    {heading}
                  </th>)}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100" data-id="element-5161">
              {orders.map(order => <Fragment key={order.id} data-id="element-5162">
                  <tr data-id="element-5163">
                    <td className="px-5 py-3.5" data-id="element-5164">
                      <p className="font-semibold text-slate-900" data-id="element-5165">
                        {order.orderNumber}
                      </p>
                      <p className="mt-1 text-xs text-slate-500" data-id="element-5166">
                        {order.kind === 'Receipt' ? 'Delivery receipt' : 'Procurement plan'}
                      </p>
                    </td>
                    <td className="px-5 py-3.5 text-sm text-slate-700" data-id="element-5167">
                      <p data-id="element-5168">{order.supplierName}</p>
                      <p className="mt-1 text-xs text-slate-500" data-id="element-5169">
                        {order.invoiceNumber ? `Invoice ${order.invoiceNumber}` : 'Invoice recorded on receipt'}
                      </p>
                    </td>
                    <td className="px-5 py-3.5 text-sm text-slate-600" data-id="element-5170">
                      {order.dateReceived ? <>
                          <p data-id="element-5171">
                            {new Date(order.dateReceived).toLocaleDateString()}
                          </p>
                          <p className="mt-1 text-xs text-slate-500" data-id="element-5172">
                            {order.receivedByName}
                          </p>
                        </> : order.expectedDate ? `Expected ${new Date(`${order.expectedDate}T00:00:00`).toLocaleDateString()}` : '—'}
                    </td>
                    <td className="px-5 py-3.5 text-sm font-medium text-slate-700" data-id="element-5173">
                      {formatCurrency(order.totalCost ?? order.lines.reduce((sum, line) => sum + line.quantityOrdered * line.unitCost, 0))}
                    </td>
                    <td className="px-5 py-3.5" data-id="element-5174">
                      <Status status={order.status} data-id="element-5175" />
                    </td>
                    <td className="px-5 py-3.5 text-right" data-id="element-5176">
                      <div className="flex justify-end gap-3" data-id="element-5177">
                        {order.status === 'Draft' && <button type="button" onClick={() => updatePurchaseOrder(order.id, {
                      status: 'Pending'
                    })} className="text-sm font-semibold text-teal-700 hover:text-teal-900" data-id="element-5178">
                            Submit
                          </button>}
                        {manager && order.kind !== 'Receipt' && !['Received', 'Cancelled'].includes(order.status) && <button type="button" disabled={cancellingId === order.id} onClick={() => cancelOrder(order.id)} className="inline-flex items-center text-sm font-semibold text-red-700 hover:text-red-900 disabled:opacity-50" data-id="element-5179">
                              <BanIcon className="mr-1 h-4 w-4" data-id="element-5180" />
                              {cancellingId === order.id ? 'Cancelling…' : 'Cancel'}
                            </button>}
                        <button type="button" onClick={() => setExpandedId(expandedId === order.id ? null : order.id)} className="inline-flex items-center gap-1 text-sm font-semibold text-slate-600 hover:text-slate-900" data-id="element-5181">
                          Items{' '}
                          {expandedId === order.id ? <ChevronUpIcon className="h-4 w-4" data-id="element-5182" /> : <ChevronDownIcon className="h-4 w-4" data-id="element-5183" />}
                        </button>
                      </div>
                    </td>
                  </tr>
                  {expandedId === order.id && <tr className="bg-slate-50/60" data-id="element-5184">
                      <td colSpan={6} className="px-5 py-4" data-id="element-5185">
                        <table className="min-w-full text-sm" data-id="element-5186">
                          <thead className="text-left text-xs uppercase tracking-wide text-slate-500" data-id="element-5187">
                            <tr data-id="element-5188">
                              <th className="pb-2" data-id="element-5189">Medicine</th>
                              <th className="pb-2" data-id="element-5190">Batch</th>
                              <th className="pb-2" data-id="element-5191">Quantity</th>
                              <th className="pb-2" data-id="element-5192">Buying price</th>
                              <th className="pb-2" data-id="element-5193">Received</th>
                            </tr>
                          </thead>
                          <tbody data-id="element-5194">
                            {order.lines.map(line => <tr key={`${order.id}-${line.medicineId}-${line.batchNumber || ''}`} data-id="element-5195">
                                <td className="py-1.5 font-medium text-slate-800" data-id="element-5196">
                                  {line.medicineName}
                                </td>
                                <td className="py-1.5 text-slate-600" data-id="element-5197">
                                  {line.batchNumber || '—'}
                                </td>
                                <td className="py-1.5 text-slate-600" data-id="element-5198">
                                  {line.quantityOrdered}
                                </td>
                                <td className="py-1.5 text-slate-600" data-id="element-5199">
                                  {formatCurrency(line.buyingPrice ?? line.unitCost)}
                                </td>
                                <td className="py-1.5 text-slate-600" data-id="element-5200">
                                  {line.quantityReceived}
                                </td>
                              </tr>)}
                          </tbody>
                        </table>
                      </td>
                    </tr>}
                </Fragment>)}
              {!orders.length && <tr data-id="element-5201">
                  <td colSpan={6} className="px-5 py-14 text-center text-sm text-slate-500" data-id="element-5202">
                    No purchase orders or receipts yet.
                  </td>
                </tr>}
            </tbody>
          </table>
        </div>
      </section>
    </div>;
}
function Status({
  status
}: {
  status: string;
}) {
  const colors: Record<string, string> = {
    Draft: 'bg-slate-100 text-slate-700',
    Pending: 'bg-blue-50 text-blue-700',
    'Partially Received': 'bg-amber-50 text-amber-700',
    Received: 'bg-emerald-50 text-emerald-700',
    Cancelled: 'bg-red-50 text-red-700'
  };
  return <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-semibold ${colors[status] || 'bg-slate-100 text-slate-700'}`} data-id="element-5203">
      {status}
    </span>;
}